import { z } from "zod";
import {
  MAX_STOCK_QTY_Q4,
  POS_MAX_PAYMENTS,
  isValidCsc,
  isValidCscId,
  type CashCloseLine,
  type PosPaymentKind,
  type ScaleConfig,
} from "@cx/domain";
import { ulidParam } from "./ids";

// ===== PDV e controle de caixa (T3.1, RF-VEN-05/06) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);
const text = (max: number) =>
  nullish(
    z
      .string()
      .trim()
      .max(max, `Use até ${max} caracteres`)
      .transform((s) => s || null),
  );
const cents = (what: string) =>
  z
    .number(`Informe ${what}`)
    .int("Use centavos inteiros")
    .min(0, `${what} não pode ser negativo`)
    .max(1_000_000_000_00, `${what} acima do limite`);
const positiveCents = (what: string) => cents(what).refine((v) => v > 0, `${what} deve ser maior que zero`);
const pct4 = z
  .number("Informe o percentual")
  .int("Percentual inválido")
  .min(0, "O percentual não pode ser negativo")
  .max(1_000_000, "O percentual vai até 100%");

export const POS_MAX_ITEMS = 300;
export const POS_MAX_SYNC_BATCH = 50;
export const POS_MAX_TERMINALS = 50;

// ---------- configuração ----------

export const posSettingsInput = z.object({
  /** Limite de desconto de quem não tem regra do papel (pct4; 10% = 100.000). */
  defaultDiscountLimitPct4: pct4.default(100_000),
  /** Limite por papel (id do papel no controle de acesso). */
  roleDiscountLimits: z
    .array(z.object({ roleId: z.string().trim().min(1).max(40), limitPct4: pct4 }))
    .max(50)
    .default([]),
  scale: z
    .object({
      prefix: z.string().regex(/^2\d?$/, "Prefixo: 2 ou de 20 a 29"),
      codeLength: z.number().int().min(4, "Código: de 4 a 6 dígitos").max(6, "Código: de 4 a 6 dígitos"),
      valueKind: z.enum(["price", "weight"]),
    })
    .default({ prefix: "2", codeLength: 5, valueKind: "price" }),
  /** Emite NFC-e em toda venda (desligado = só o comprovante não fiscal). */
  issueNfce: z.boolean().default(true),
  /** Prazo para transmitir a NFC-e em contingência (horas). */
  contingencyHours: z.number().int().min(1).max(168).default(24),
});
export type PosSettingsInput = z.output<typeof posSettingsInput>;
export type PosSettingsView = PosSettingsInput & { scale: ScaleConfig; consumerContactId: string | null };

export const nfceCscInput = z.object({
  cscId: z
    .string("Informe o ID do CSC")
    .trim()
    .refine(isValidCscId, "ID do CSC: de 1 a 6 dígitos (ex.: 000001)"),
  csc: z
    .string("Informe o CSC")
    .trim()
    .refine(isValidCsc, "CSC: de 16 a 36 letras e números, como fornecido pela SEFAZ"),
});
export type NfceCscInput = z.output<typeof nfceCscInput>;
export type NfceCscView = {
  configured: boolean;
  cscId: string | null;
  last4: string | null;
  updatedAt: string | null;
};

export const posTerminalInput = z.object({
  name: z.string("Informe o nome").trim().min(1, "Informe o nome").max(60, "Use até 60 caracteres"),
  cashAccountId: ulidParam,
  warehouseId: nullish(ulidParam),
  priceListId: nullish(ulidParam),
  nfceSeries: z.number().int().min(0).max(889, "Série de 0 a 889").default(1),
  contingencyMode: z.boolean().default(false),
  active: z.boolean().default(true),
});
export type PosTerminalInput = z.output<typeof posTerminalInput>;
export type PosTerminalView = PosTerminalInput & {
  id: string;
  cashAccountName: string | null;
  openSession: { id: string; number: number; userId: string; openedAt: string } | null;
};

// ---------- caixa ----------

export const cashOpenInput = z.object({
  terminalId: ulidParam,
  openingCents: cents("o fundo de troco").default(0),
});
export type CashOpenInput = z.output<typeof cashOpenInput>;

export const cashMovementInput = z.object({
  kind: z.enum(["supply", "withdrawal"], "Escolha suprimento ou sangria"),
  amountCents: positiveCents("o valor"),
  /** Conta de origem (suprimento) ou destino (sangria): vira transferência entre contas. */
  counterpartAccountId: nullish(ulidParam),
  notes: text(200),
});
export type CashMovementInput = z.output<typeof cashMovementInput>;

const countedCents = nullish(cents("o valor contado"));
export const cashCloseInput = z.object({
  counted: z.object({
    cash: cents("o dinheiro contado"),
    pix: countedCents,
    card: countedCents,
    credit: countedCents,
    other: countedCents,
  }),
  notes: text(500),
});
export type CashCloseInput = z.output<typeof cashCloseInput>;

export type CashMovementView = {
  id: string;
  kind: "sale" | "change" | "supply" | "withdrawal" | "refund" | "adjustment";
  method: PosPaymentKind;
  amountCents: number;
  originType: string | null;
  originId: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
};
export type CashSessionView = {
  id: string;
  number: number;
  terminalId: string;
  terminalName: string;
  userId: string;
  status: "open" | "closed";
  openingCents: number;
  openedAt: string;
  closedAt: string | null;
  closedBy: string | null;
  salesCount: number;
  /** Fechamento cego: esperado e diferença só depois de fechado (ou para quem tem pos:update). */
  lines: CashCloseLine[] | null;
  differenceCents: number | null;
  notes: string | null;
  movements: CashMovementView[];
};
export type CashSessionListItem = Omit<CashSessionView, "movements" | "lines">;

export const cashSessionListQuery = z.object({
  status: z.enum(["open", "closed"]).optional(),
  terminalId: ulidParam.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type CashSessionListQuery = z.output<typeof cashSessionListQuery>;

// ---------- venda ----------

export const posSaleItemInput = z.object({
  productId: ulidParam,
  qtyQ4: z
    .number("Informe a quantidade")
    .int("Quantidade inválida")
    .min(1, "A quantidade deve ser maior que zero")
    .max(MAX_STOCK_QTY_Q4, "Quantidade acima do limite"),
  /** Preço unitário mostrado no terminal (o servidor recalcula). */
  unitPriceCents: cents("o preço"),
  discountPct4: pct4.default(0),
});

export const posPaymentInput = z.object({
  paymentMethodId: ulidParam,
  amountCents: positiveCents("o valor do pagamento"),
  /** Crediário: parcelas mensais (1 a 12). */
  installments: z.number().int().min(1).max(12).default(1),
});

export const posSaleInput = z
  .object({
    /** Id gerado no terminal (ULID): chave de idempotência da venda (também da fila offline). */
    clientId: ulidParam,
    terminalId: ulidParam,
    cashSessionId: ulidParam,
    /** Hora da venda no terminal (ISO). */
    soldAt: z.iso.datetime({ offset: true, error: "Hora da venda inválida" }),
    offline: z.boolean().default(false),
    /** Cliente identificado (crediário exige); nulo = consumidor final. */
    contactId: nullish(ulidParam),
    consumerDocument: nullish(
      z
        .string()
        .transform((s) => s.replace(/\D/g, ""))
        .pipe(z.string().regex(/^(\d{11}|\d{14})?$/, "CPF/CNPJ inválido"))
        .transform((s) => s || null),
    ),
    consumerName: text(60),
    items: z
      .array(posSaleItemInput)
      .min(1, "Inclua ao menos um item")
      .max(POS_MAX_ITEMS, `No máximo ${POS_MAX_ITEMS} itens`),
    discountCents: cents("o desconto").default(0),
    payments: z
      .array(posPaymentInput)
      .min(1, "Informe o pagamento")
      .max(POS_MAX_PAYMENTS, `No máximo ${POS_MAX_PAYMENTS} pagamentos`),
    /** Total que o terminal mostrou (confere com o recalculado). */
    clientTotalCents: cents("o total"),
  })
  .strict();
export type PosSaleInput = z.output<typeof posSaleInput>;

export const posSyncInput = z.object({
  sales: z.array(posSaleInput).min(1).max(POS_MAX_SYNC_BATCH),
});
export type PosSyncInput = z.output<typeof posSyncInput>;

export const posCancelInput = z.object({
  reason: z
    .string("Informe o motivo")
    .trim()
    .min(15, "Motivo: ao menos 15 caracteres")
    .max(255, "Motivo: até 255 caracteres"),
});
export type PosCancelInput = z.output<typeof posCancelInput>;

export const posReturnInput = z.object({
  cashSessionId: ulidParam,
  items: z
    .array(z.object({ productId: ulidParam, qtyQ4: z.number().int().min(1).max(MAX_STOCK_QTY_Q4) }))
    .min(1, "Escolha os itens devolvidos")
    .max(POS_MAX_ITEMS),
  /** Dinheiro: sai do caixa; nenhum = troca (o cliente leva outro produto numa nova venda). */
  refund: z.enum(["cash", "none"]),
  reason: z.string("Informe o motivo").trim().min(3, "Informe o motivo").max(255),
});
export type PosReturnInput = z.output<typeof posReturnInput>;

export type PosSaleNfce = {
  id: string;
  status: string;
  number: number | null;
  series: number | null;
  accessKey: string | null;
  emissionType: number;
  rejectionReason: string | null;
};
export type PosSalePayment = {
  paymentMethodId: string;
  name: string;
  kind: PosPaymentKind;
  sefazType: string | null;
  amountCents: number;
  appliedCents: number;
  installments: number;
};
export type PosSaleView = {
  id: string;
  clientId: string;
  number: number;
  status: "completed" | "canceled";
  offline: boolean;
  soldAt: string;
  terminalId: string;
  terminalName: string;
  cashSessionId: string;
  userId: string;
  salesOrderId: string;
  salesOrderNumber: number;
  contactId: string;
  contactName: string;
  consumerDocument: string | null;
  consumerName: string | null;
  items: {
    productId: string | null;
    code: string | null;
    description: string;
    unit: string;
    qtyQ4: number;
    unitPriceCents: number;
    totalCents: number;
  }[];
  grossCents: number;
  discountCents: number;
  totalCents: number;
  paidCents: number;
  changeCents: number;
  clientTotalCents: number | null;
  divergenceCents: number;
  shortfallCents: number;
  payments: PosSalePayment[];
  nfce: PosSaleNfce | null;
  returns: {
    id: string;
    totalCents: number;
    refundMethod: "cash" | "none";
    reason: string;
    createdAt: string;
  }[];
  canceledAt: string | null;
  cancelReason: string | null;
};
export type PosSaleListItem = Pick<
  PosSaleView,
  | "id"
  | "number"
  | "status"
  | "offline"
  | "soldAt"
  | "terminalName"
  | "contactName"
  | "totalCents"
  | "divergenceCents"
  | "nfce"
>;
export type PosSaleCreated = {
  id: string;
  number: number;
  totalCents: number;
  changeCents: number;
  /** Venda já registrada antes (mesmo `clientId`): a resposta é a da primeira vez. */
  replayed: boolean;
  divergenceCents: number;
  nfce: PosSaleNfce | null;
};
export type PosSyncResult = {
  results: (
    | { clientId: string; ok: true; sale: PosSaleCreated }
    | { clientId: string; ok: false; code: string; message: string }
  )[];
};

export const posSaleListQuery = z.object({
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  status: z.enum(["completed", "canceled"]).optional(),
  terminalId: ulidParam.optional(),
  cashSessionId: ulidParam.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type PosSaleListQuery = z.output<typeof posSaleListQuery>;

// ---------- catálogo offline ----------

export type PosCatalogProduct = {
  id: string;
  sku: string | null;
  gtin: string | null;
  name: string;
  unit: string;
  priceCents: number;
  /** Vendido por peso (unidade KG): aceita etiqueta de balança e quantidade fracionada. */
  weighed: boolean;
};
export type PosCatalogCustomer = { id: string; name: string; document: string | null };
export type PosPaymentMethodOption = {
  id: string;
  name: string;
  kind: PosPaymentKind;
  sefazType: string | null;
};
export type PosBootstrap = {
  serverTime: string;
  companyId: string;
  companyName: string;
  settings: PosSettingsView;
  /** Limite de desconto do usuário atual (pct4). */
  discountLimitPct4: number;
  terminals: PosTerminalView[];
  paymentMethods: PosPaymentMethodOption[];
  /** Caixa aberto do usuário (qualquer terminal). */
  openSession: CashSessionListItem | null;
  canUpdate: boolean;
};
export type PosCatalog = {
  generatedAt: string;
  products: PosCatalogProduct[];
  customers: PosCatalogCustomer[];
  truncated: boolean;
};
